import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, AlertCircle, X, Zap, Trophy, Star, Sparkles, CircleHelp } from "lucide-react";
import QuestionCard from "../components/QuestionCard";
import { FirstVisitTour, replayTour } from "../components/FirstVisitTour";
import SuccessModal from "../components/SuccessModal";
import AuthModal from "../components/AuthModal";
import FlappyBat from "../components/FlappyBat";
import CodeWorkspace from "../components/CodeWorkspace";
import { isBlockMode } from "../lib/modes";
import { saveBlocksSolution } from "../components/blocks/solutionTools";
import { useMode } from "../context/ModeContext";
import ProgressHeader from "../components/ProgressHeader";
import TimerDisplay from "../components/Timer";
import NoQuestionsCard from "../components/NoQuestionsCard";
import DrillMode from "../components/DrillMode";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

// Use empty string for same-origin API calls (works on both preview and production)
// The /api prefix routes to the backend through the ingress
const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const DAILY_QUESTIONS_REQUIRED = 3;
const ANONYMOUS_CHALLENGE_LIMIT = 1;
const LOGGED_IN_CHALLENGE_LIMIT = 3;
const SUBSCRIBER_CHALLENGE_LIMIT = 8;

// Timer constants (in seconds)
const BLITZ_TIMER_SECONDS = 3 * 60; // 3 minutes for blitz bonus (matches backend BLITZ_BONUS_WINDOW_SECONDS)
const BLITZ_BONUS_POINTS = 10;

// Blocks and Python are separate tracks: anonymous progress + persisted question are stored per mode.
// `activeMode` is set on every render from the ModeContext (the page remounts when the mode changes).
let activeMode = "python";
const k = (key) => (activeMode && activeMode !== "python" ? `${activeMode}_${key}` : key);
const withMode = (url) => `${url}${url.includes("?") ? "&" : "?"}mode=${activeMode}`;

// Get today's date in Australian timezone (AEST/AEDT)
const getAustralianDate = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
};

// Session storage helpers for anonymous users
const getSessionData = () => {
  try {
    const data = sessionStorage.getItem(k('dailyCodingSession'));
    if (data) {
      const parsed = JSON.parse(data);
      // Check if it's from today (Australian time)
      const today = getAustralianDate();
      if (parsed.date === today) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error reading session data:', e);
  }
  return {
    date: getAustralianDate(),
    answeredQuestions: [],
    sessionScore: 0,
    dailyQuestionsCompleted: 0,
    challengesCompleted: 0
  };
};

const saveSessionData = (data) => {
  try {
    sessionStorage.setItem(k('dailyCodingSession'), JSON.stringify({
      ...data,
      date: getAustralianDate()
    }));
  } catch (e) {
    console.error('Error saving session data:', e);
  }
};

// Persist current question to localStorage to prevent refresh cheating


// Built-in fallback questions (served while a track has no questions yet) must never be cached - they aren't in the database
const isFallbackQuestion = (q) => !!q && (q.fallback === true || q.question_id === "q_default_junior_001" || q.question_id === "q_default_blocks_001");

const saveCurrentQuestion = (question, pool) => {
  if (!question || isFallbackQuestion(question)) return;
  try {
    const today = getAustralianDate();
    localStorage.setItem(k('currentQuestion'), JSON.stringify({
      question,
      pool, // 'daily' or 'challenge'
      date: today,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('Error saving current question:', e);
  }
};

const loadCurrentQuestion = (pool) => {
  try {
    const data = localStorage.getItem(k('currentQuestion'));
    if (!data) return null;
    
    const parsed = JSON.parse(data);
    const today = getAustralianDate();
    
    // Only return if it's from today (Australian time) and matches the requested pool
    if (parsed.date === today && parsed.pool === pool && parsed.question) {
      if (isFallbackQuestion(parsed.question)) {
        localStorage.removeItem(k('currentQuestion'));
        return null;
      }
      // Additional validation: check if the question itself is valid for today
      // If not valid, log warning but still return null to trigger fresh fetch
      if (!isQuestionValidForToday(parsed.question)) {
        console.warn('Cached question has mismatched date, fetching fresh');
        localStorage.removeItem(k('currentQuestion'));
        return null;
      }
      return parsed.question;
    }
    
    // Clear stale data (wrong date or pool)
    localStorage.removeItem(k('currentQuestion'));
  } catch (e) {
    console.error('Error loading current question:', e);
    localStorage.removeItem(k('currentQuestion'));
  }
  return null;
};

const clearCurrentQuestion = () => {
  try {
    localStorage.removeItem(k('currentQuestion'));
  } catch (e) {
    console.error('Error clearing current question:', e);
  }
};

// Validate that a question is appropriate for today
// Returns true if question has no date OR has today's date (MM-DD format)
const isQuestionValidForToday = (question) => {
  if (!question) return false;
  
  const questionDate = question.date;
  
  // No date means it can appear any day
  if (!questionDate || questionDate === '') return true;
  
  // Check if question's date matches today (Australian time, MM-DD format)
  const today = getAustralianDate(); // Returns YYYY-MM-DD
  const todayMMDD = today.substring(5); // Extract MM-DD
  
  return questionDate === todayMMDD;
};

export default function DailyChallenge({ user, onMergeSession, onScoreUpdate }) {
  const { mode: siteMode } = useMode();
  activeMode = siteMode;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dailyQuestion, setDailyQuestion] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null); // Current challenge question
  const [challenges, setChallenges] = useState([]);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [completedChallenges, setCompletedChallenges] = useState(new Set());
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastPointsEarned, setLastPointsEarned] = useState(10);
  const [lastWasChallenge, setLastWasChallenge] = useState(false);
  const [lastAlreadyAnswered, setLastAlreadyAnswered] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0); // Counter to force CodeEditor reset
  
  // Initialize mode from localStorage if there's a recent merged auth state
  const [mode, setMode] = useState(() => {
    try {
      // First check for date-based persistence (most reliable)
      const today = getAustralianDate();
      const savedCompletion = localStorage.getItem(k('dailyWarmUpCompleted'));
      if (savedCompletion) {
        const parsed = JSON.parse(savedCompletion);
        if (parsed.date === today && parsed.completed) {
          console.log("Initializing mode to challenge from dailyWarmUpCompleted");
          return "challenge";
        }
      }
      
      // Fallback: check mergedAuthState (for login flow)
      const mergedState = localStorage.getItem('mergedAuthState');
      if (mergedState) {
        const parsed = JSON.parse(mergedState);
        if (Date.now() - parsed.timestamp < 30000 && parsed.startInChallengeMode) {
          console.log("Initializing mode to challenge from localStorage");
          return "challenge";
        }
      }
      const appliedFlag = localStorage.getItem('mergedStateAppliedFlag');
      if (appliedFlag) {
        const parsed = JSON.parse(appliedFlag);
        if (Date.now() - parsed.timestamp < 30000) {
          console.log("Initializing mode to challenge from appliedFlag");
          return "challenge";
        }
      }
    } catch (e) {
      console.error("Error reading mode from localStorage:", e);
    }
    return "daily";
  });
  
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [dailyProgress, setDailyProgress] = useState(() => {
    try {
      const mergedState = localStorage.getItem('mergedAuthState');
      if (mergedState) {
        const parsed = JSON.parse(mergedState);
        if (Date.now() - parsed.timestamp < 30000 && parsed.warmUpProgress >= DAILY_QUESTIONS_REQUIRED) {
          return {
            completed: parsed.warmUpProgress,
            required: DAILY_QUESTIONS_REQUIRED
          };
        }
      }
    } catch (e) {
      console.error("Error reading dailyProgress from localStorage:", e);
    }
    return {
      completed: 0,
      required: DAILY_QUESTIONS_REQUIRED
    };
  });
  
  // Initialize dailyCompleted from localStorage
  const [dailyCompleted, setDailyCompleted] = useState(() => {
    try {
      // First check for date-based persistence (most reliable)
      const today = getAustralianDate();
      const savedCompletion = localStorage.getItem(k('dailyWarmUpCompleted'));
      if (savedCompletion) {
        const parsed = JSON.parse(savedCompletion);
        if (parsed.date === today && parsed.completed) {
          return true;
        }
      }
      
      // Fallback: check mergedAuthState (for login flow)
      const mergedState = localStorage.getItem('mergedAuthState');
      if (mergedState) {
        const parsed = JSON.parse(mergedState);
        if (Date.now() - parsed.timestamp < 30000 && parsed.startInChallengeMode) {
          return true;
        }
      }
      const appliedFlag = localStorage.getItem('mergedStateAppliedFlag');
      if (appliedFlag) {
        const parsed = JSON.parse(appliedFlag);
        if (Date.now() - parsed.timestamp < 30000) {
          return true;
        }
      }
    } catch (e) {
      console.error("Error reading dailyCompleted from localStorage:", e);
    }
    return false;
  });
  const [sessionData, setSessionData] = useState(getSessionData());
  
  // Initialize challengesInfo from localStorage or default
  const [challengesInfo, setChallengesInfo] = useState(() => {
    // Try to load from localStorage for persistence across navigation
    const stored = localStorage.getItem(k('codingDrills_challengesInfo'));
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Only use if it's from today
        const today = getAustralianDate();
        if (parsed.date === today) {
          return {
            challenges_today: parsed.challenges_today || 0,
            can_do_more: parsed.can_do_more !== false,
            daily_limit: parsed.daily_limit || (user ? LOGGED_IN_CHALLENGE_LIMIT : ANONYMOUS_CHALLENGE_LIMIT)
          };
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return {
      challenges_today: 0,
      can_do_more: true,
      daily_limit: user ? LOGGED_IN_CHALLENGE_LIMIT : ANONYMOUS_CHALLENGE_LIMIT
    };
  });
  const [showKeepPlayingModal, setShowKeepPlayingModal] = useState(false);
  const [allChallengesDone, setAllChallengesDone] = useState(false);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [streakBonus, setStreakBonus] = useState(0); // Track streak bonus earned
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mergedStateApplied, setMergedStateApplied] = useState(false); // Flag to prevent fetchDailyProgress from overwriting
  const mergedStateAppliedRef = useRef(false); // Ref for synchronous access
  const [revealedQuestions, setRevealedQuestions] = useState(new Set()); // Track questions with revealed answers
  const [lastAnswerRevealed, setLastAnswerRevealed] = useState(false); // Track if last correct answer was revealed
  const [subscriptionTier, setSubscriptionTier] = useState("free"); // Track subscription tier
  const [countdownToMidnight, setCountdownToMidnight] = useState({ hours: 0, minutes: 0, seconds: 0 });
  
  // Cave Bat game state
  const [showCaveBat, setShowCaveBat] = useState(false);
  // Cave Bat easter egg (Konami code) overlay
  const [showCaveBatEgg, setShowCaveBatEgg] = useState(false);
  
  // Drill state
  const [assignedDrill, setAssignedDrill] = useState(null);
  const [assignedDrills, setAssignedDrills] = useState([]);
  const [drillMode, setDrillMode] = useState(false);
  const [selectedDrill, setSelectedDrill] = useState(null); // The drill user chose to work on
  const [adminPreviewDrill, setAdminPreviewDrill] = useState(null); // For school admin drill preview
  const drillPollingRef = useRef(null); // Ref to track polling interval
  
  // Countdown to Australian midnight
  useEffect(() => {
    const calculateTimeToMidnight = () => {
      // Get current time in Australia/Sydney
      const now = new Date();
      const australianTime = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
      
      // Calculate midnight in Australian time
      const midnight = new Date(australianTime);
      midnight.setHours(24, 0, 0, 0);
      
      // Calculate difference in seconds
      const diffMs = midnight - australianTime;
      const diffSeconds = Math.floor(diffMs / 1000);
      
      const hours = Math.floor(diffSeconds / 3600);
      const minutes = Math.floor((diffSeconds % 3600) / 60);
      const seconds = diffSeconds % 60;
      
      setCountdownToMidnight({ hours, minutes, seconds });
    };
    
    // Calculate immediately
    calculateTimeToMidnight();
    
    // Update every second
    const interval = setInterval(calculateTimeToMidnight, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Konami code easter egg -> launch Cave Bat (logged-in only; prompt login otherwise)
  // Disabled during drills (async drill mode) so it can't interrupt an active drill.
  useEffect(() => {
    if (drillMode) return;
    const sequence = [
      "arrowup", "arrowup", "arrowdown", "arrowdown",
      "arrowleft", "arrowright", "arrowleft", "arrowright",
      "b", "a"
    ];
    let buffer = [];
    const handler = (e) => {
      buffer.push(e.key.toLowerCase());
      if (buffer.length > sequence.length) {
        buffer = buffer.slice(-sequence.length);
      }
      if (buffer.length === sequence.length && buffer.every((k, i) => k === sequence[i])) {
        buffer = [];
        if (user) {
          setShowCaveBatEgg(true);
        } else {
          toast.info("You found the secret code! Log in to play Cave Bat 🦇");
          setShowAuthModal(true);
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [user, drillMode]);

  // Close the Cave Bat easter egg overlay with Escape
  useEffect(() => {
    if (!showCaveBatEgg) return;
    const onEsc = (e) => {
      if (e.key === "Escape") setShowCaveBatEgg(false);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [showCaveBatEgg]);
  
  // Persist challengesInfo to localStorage whenever it changes (for navigation persistence)
  useEffect(() => {
    const today = getAustralianDate();
    localStorage.setItem(k('codingDrills_challengesInfo'), JSON.stringify({
      ...challengesInfo,
      date: today
    }));
  }, [challengesInfo]);
  
  // Persist dailyCompleted to localStorage whenever it changes
  useEffect(() => {
    if (dailyCompleted) {
      const today = getAustralianDate();
      localStorage.setItem(k('dailyWarmUpCompleted'), JSON.stringify({
        date: today,
        completed: true
      }));
    }
  }, [dailyCompleted]);
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0); // Time remaining in seconds
  const [timerActive, setTimerActive] = useState(false);
  const [blitzBonusEarned, setBlitzBonusEarned] = useState(false); // Track if blitz bonus was earned
  const [lastBlitzBonus, setLastBlitzBonus] = useState(0); // Track blitz bonus for success modal
  const [lastGemBonus, setLastGemBonus] = useState(0); // Hidden gem bonus (Junior Blocks) for success modal
  const timerRef = useRef(null);

  // Check if we should restore state after login (warm-up progress or challenge mode)
  useEffect(() => {
    if (location.state && user) {
      // User logged in with session data merged
      if (location.state.pointsMerged) {
        toast.success("Welcome! Your progress has been saved to your account.");
      }
      
      if (location.state.startInChallengeMode) {
        // User was in challenge mode when they logged in
        setDailyCompleted(true);
        setMode("challenge");
        fetchChallenges();
      } else if (location.state.warmUpProgress !== undefined) {
        // User was making warm-up progress - restore it
        const warmUpCount = location.state.warmUpProgress;
        setDailyProgress({
          completed: warmUpCount,
          required: DAILY_QUESTIONS_REQUIRED
        });
        
        // If they completed 3 warm-ups before login, unlock challenges
        if (warmUpCount >= DAILY_QUESTIONS_REQUIRED) {
          setDailyCompleted(true);
          fetchChallenges();
        }
      }
      
      // Clear the location state after processing to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user]);
  
  // Check for merged auth state from localStorage (set during login/register)
  // Using useLayoutEffect to ensure this runs synchronously before other effects
  useLayoutEffect(() => {
    if (user) {
      try {
        const mergedState = localStorage.getItem('mergedAuthState');
        if (mergedState) {
          const parsed = JSON.parse(mergedState);
          // Only use if recent (within last 30 seconds)
          if (Date.now() - parsed.timestamp < 30000) {
            console.log("Restoring merged auth state:", parsed);
            console.log("startInChallengeMode:", parsed.startInChallengeMode);
            // Set both state and ref to prevent race conditions
            setMergedStateApplied(true);
            mergedStateAppliedRef.current = true;
            
            // Also set a flag in localStorage to survive component re-mounts
            localStorage.setItem('mergedStateAppliedFlag', JSON.stringify({ timestamp: Date.now() }));
            
            if (parsed.startInChallengeMode) {
              console.log("Setting mode to challenge...");
              // User completed warm-ups before logging in - go straight to challenges
              setDailyCompleted(true);
              setMode("challenge");
              console.log("Mode set to challenge");
              setDailyProgress({
                completed: parsed.warmUpProgress || DAILY_QUESTIONS_REQUIRED,
                required: DAILY_QUESTIONS_REQUIRED
              });
              
              // Also update challenges info if they did some challenges
              if (parsed.challengesCompleted > 0) {
                setChallengesInfo((prev) => ({
                  ...prev,
                  challenges_today: parsed.challengesCompleted,
                  can_do_more: parsed.challengesCompleted < (user ? LOGGED_IN_CHALLENGE_LIMIT : ANONYMOUS_CHALLENGE_LIMIT)
                }));
              }
              
              fetchChallenges();
              toast.success("Welcome! Your progress has been saved to your account.");
              
              // Trigger navbar refresh to show updated score
              if (onScoreUpdate) {
                setTimeout(() => onScoreUpdate(), 500);
              }
            } else if (parsed.warmUpProgress > 0) {
              // Partial warm-up progress
              setDailyProgress({
                completed: parsed.warmUpProgress,
                required: DAILY_QUESTIONS_REQUIRED
              });
              toast.success("Welcome! Your progress has been saved to your account.");
              
              // Trigger navbar refresh
              if (onScoreUpdate) {
                setTimeout(() => onScoreUpdate(), 500);
              }
            }
          }
          // Always clear after reading
          localStorage.removeItem('mergedAuthState');
        }
      } catch (e) {
        console.error("Error reading merged auth state:", e);
        localStorage.removeItem('mergedAuthState');
      }
    }
  }, [user]);

  useEffect(() => {
    // Check dark mode
    const isDark = document.documentElement.classList.contains("dark");
    setDarkMode(isDark);
    
    // Watch for dark mode changes
    const observer = new MutationObserver(() => {
      setDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true });
    
    // Merge session data if user just logged in
    if (user && sessionData.answeredQuestions.length > 0) {
      mergeSessionWithAccount();
    }
    
    return () => observer.disconnect();
  }, []);
  
  // Fetch daily question - runs after auth check completes or for anonymous users
  useEffect(() => {
    console.log("Initial useEffect running - user:", !!user, "dailyCompleted:", dailyCompleted);
    
    // If dailyCompleted is true from localStorage, start in challenge mode
    if (dailyCompleted) {
      console.log("dailyCompleted is true - switching to challenge mode");
      setMode("challenge");
      fetchChallenges();
      setLoading(false);
      return;
    }
    
    // For ANONYMOUS users - ALWAYS fetch the daily question immediately
    if (!user) {
      console.log("Anonymous user detected - fetching daily question");
      fetchDailyQuestion();
      return;
    }
    
    // For LOGGED-IN users - check if there's a merged auth state that should be applied first
    const mergedState = localStorage.getItem('mergedAuthState');
    if (mergedState) {
      try {
        const parsed = JSON.parse(mergedState);
        // If there's a recent merged state (challenge mode OR partial progress), wait for the useLayoutEffect to apply it
        if (Date.now() - parsed.timestamp < 30000 && (parsed.startInChallengeMode || parsed.warmUpProgress > 0)) {
          console.log("fetchDailyQuestion - waiting for merged auth state to be applied (warmUpProgress:", parsed.warmUpProgress, ")");
          // The useLayoutEffect will handle setting the progress
          // Still need to fetch a question, but don't overwrite progress
          setLoading(false);
          
          // If not going to challenge mode, still need to fetch a daily question
          if (!parsed.startInChallengeMode) {
            // Fetch question but delay slightly to let useLayoutEffect run first
            setTimeout(() => {
              fetchDailyQuestion();
            }, 100);
          }
          return;
        }
      } catch (e) {
        console.error("Error parsing merged auth state:", e);
        localStorage.removeItem('mergedAuthState');
      }
    }
    
    // For logged-in users without merged state, fetch daily question
    fetchDailyQuestion();
  }, [user]); // Run when user changes (including initial mount)

  // Update daily progress when user changes
  useEffect(() => {
    if (user) {
      // Skip if merged state was just applied - it already set the correct state
      if (!mergedStateApplied && !mergedStateAppliedRef.current) {
        fetchDailyProgress();
      }
      // Always fetch assigned drill for logged-in users
      fetchAssignedDrill();
    } else {
      // Use session data for anonymous users
      setDailyProgress({
        completed: sessionData.dailyQuestionsCompleted,
        required: DAILY_QUESTIONS_REQUIRED
      });
      setAssignedDrill(null);
    }
  }, [user, sessionData.dailyQuestionsCompleted, mergedStateApplied]);

  // Check for startDrill URL parameter to auto-start drill from notification
  useEffect(() => {
    const shouldStartDrill = searchParams.get('startDrill') === 'true';
    if (shouldStartDrill && assignedDrill && assignedDrill.status !== 'completed') {
      setSelectedDrill(assignedDrill);
      setDrillMode(true);
      // Remove the param from URL
      searchParams.delete('startDrill');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, assignedDrill]);

  // Check for adminPreviewDrill URL parameter (for school admins to preview drills)
  useEffect(() => {
    const previewDrillId = searchParams.get('adminPreviewDrill');
    if (previewDrillId && user) {
      // Fetch the drill details and start preview mode
      const fetchPreviewDrill = async () => {
        try {
          // Use the school admin preview endpoint
          const response = await fetch(`${API}/school/drill/${previewDrillId}/preview`, {
            credentials: "include"
          });
          if (response.ok) {
            const data = await response.json();
            if (data.drill) {
              // Create a preview drill object with all needed fields
              const previewDrill = {
                drill_id: data.drill.drill_id,
                title: data.drill.title,
                description: data.drill.description,
                difficulty: data.drill.difficulty,
                total_questions: data.drill.question_count || 0,
                completed_count: 0,
                current_index: 0,
                status: "not_started",
                is_admin_preview: true // Flag to indicate this is admin preview
              };
              setAdminPreviewDrill(previewDrill);
              setSelectedDrill(previewDrill);
              setDrillMode(true);
              // Remove the param from URL
              searchParams.delete('adminPreviewDrill');
              setSearchParams(searchParams, { replace: true });
            }
          } else {
            const errorData = await response.json().catch(() => ({}));
            toast.error(errorData.detail || "Could not load drill preview");
          }
        } catch (error) {
          console.error("Error fetching preview drill:", error);
          toast.error("Failed to load drill preview");
        }
      };
      fetchPreviewDrill();
    }
  }, [searchParams, user]);

  // Poll for drill assignments for school students (every 12 seconds)
  // This allows students to see new drill assignments without refreshing
  useEffect(() => {
    // Only poll if:
    // 1. User is logged in
    // 2. User is part of a school (school_student tier or has school membership)
    // 3. Not currently in drill mode (no need to poll while doing a drill)
    const shouldPoll = user && 
      (subscriptionTier === 'school_student' || subscriptionTier === 'school') &&
      !drillMode;
    
    if (!shouldPoll) {
      // Clear any existing polling
      if (drillPollingRef.current) {
        clearInterval(drillPollingRef.current);
        drillPollingRef.current = null;
      }
      return;
    }
    
    const pollForDrillAssignment = async () => {
      try {
        const response = await fetch(`${API}/user/assigned-drill`, {
          credentials: "include"
        });
        if (response.ok) {
          const data = await response.json();
          const newDrill = data.assigned_drill;
          const newDrills = data.assigned_drills || [];
          
          // Check if new drills were added
          const currentDrillIds = assignedDrills.map(d => d.drill_id);
          const newDrillIds = newDrills.map(d => d.drill_id);
          const addedDrills = newDrills.filter(d => !currentDrillIds.includes(d.drill_id) && d.status !== 'completed');
          
          if (addedDrills.length > 0) {
            // Show toast for new drill assignments
            if (addedDrills.length === 1) {
              toast.success(
                `New Coding Drill assigned: "${addedDrills[0].title}"`, 
                { 
                  duration: 5000,
                  description: `From ${addedDrills[0].school_name}. Click the orange button to start!`
                }
              );
            } else {
              toast.success(
                `${addedDrills.length} New Coding Drills assigned!`, 
                { 
                  duration: 5000,
                  description: "Click the orange button to see all drills."
                }
              );
            }
          }
          
          // Update state
          setAssignedDrill(newDrill);
          setAssignedDrills(newDrills);
        }
      } catch (error) {
        // Silently fail polling - don't spam console
      }
    };
    
    // Start polling every 12 seconds
    drillPollingRef.current = setInterval(pollForDrillAssignment, 12000);
    
    // Cleanup on unmount or when dependencies change
    return () => {
      if (drillPollingRef.current) {
        clearInterval(drillPollingRef.current);
        drillPollingRef.current = null;
      }
    };
  }, [user, subscriptionTier, drillMode, assignedDrills]);

  // Timer effect - countdown every second
  useEffect(() => {
    if (timerActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setTimerActive(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive]);

  // Start timer when question changes - use server-provided blitz info
  useEffect(() => {
    const question = mode === "daily" ? dailyQuestion : currentChallenge;
    if (question && !showSuccess) {
      // Check if server provided blitz info
      const blitzInfo = question.blitz_info;
      
      if (!user) {
        // Anonymous users get a local timer (no server tracking)
        setTimeRemaining(BLITZ_TIMER_SECONDS);
        setTimerActive(true);
      } else if (blitzInfo && blitzInfo.blitz_eligible && blitzInfo.blitz_time_remaining > 0) {
        // Server started/restarted the window for this view
        setTimeRemaining(blitzInfo.blitz_time_remaining);
        setTimerActive(true);
      } else {
        // Already answered / bonus already claimed / no blitz info
        setTimeRemaining(0);
        setTimerActive(false);
      }
      setBlitzBonusEarned(false);
    }
  }, [
    dailyQuestion?.question_id, currentChallenge?.question_id, mode, user,
    // Re-apply when the server refreshes blitz_info for the same question (page reload path)
    dailyQuestion?.blitz_info?.blitz_eligible, dailyQuestion?.blitz_info?.blitz_time_remaining,
    currentChallenge?.blitz_info?.blitz_eligible, currentChallenge?.blitz_info?.blitz_time_remaining,
  ]);

  // Stop timer when showing success modal
  useEffect(() => {
    if (showSuccess) {
      setTimerActive(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  }, [showSuccess]);

  // Coming back to the tab restarts the blitz window (server-side for logged-in users)
  useEffect(() => {
    const question = mode === "daily" ? dailyQuestion : currentChallenge;
    if (!question || showSuccess || drillMode) return;

    const restartBlitz = async () => {
      if (document.visibilityState !== "visible") return;
      if (!user) {
        setTimeRemaining(BLITZ_TIMER_SECONDS);
        setTimerActive(true);
        return;
      }
      try {
        const res = await fetch(`${API}/questions/blitz-status/${question.question_id}`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        if (data.blitz_eligible && data.blitz_time_remaining > 0) {
          setTimeRemaining(data.blitz_time_remaining);
          setTimerActive(true);
        } else {
          setTimeRemaining(0);
          setTimerActive(false);
        }
      } catch (e) {
        console.error("Error restarting blitz timer:", e);
      }
    };

    document.addEventListener("visibilitychange", restartBlitz);
    return () => document.removeEventListener("visibilitychange", restartBlitz);
  }, [dailyQuestion?.question_id, currentChallenge?.question_id, mode, user, showSuccess, drillMode]);

  // Helper to format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if blitz bonus is available (timer still has time)
  const canEarnBlitzBonus = () => {
    return timerActive && timeRemaining > 0;
  };

  const mergeSessionWithAccount = async () => {
    if (!user || sessionData.answeredQuestions.length === 0) return;
    
    try {
      const response = await fetch(`${API}/user/merge-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          mode: activeMode,
          answered_questions: sessionData.answeredQuestions
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.merged > 0) {
          toast.success(`Added ${result.points_added} points from your session!`);
        }
        // Clear session data after merge
        setSessionData({
          date: getAustralianDate(),
          answeredQuestions: [],
          sessionScore: 0,
          dailyQuestionsCompleted: 0
        });
        saveSessionData({
          answeredQuestions: [],
          sessionScore: 0,
          dailyQuestionsCompleted: 0
        });
        
        if (onMergeSession) {
          onMergeSession();
        }
      }
    } catch (error) {
      console.error("Error merging session:", error);
    }
  };

  const fetchDailyProgress = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(withMode(`${API}/user/daily-progress`), {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setDailyProgress({
          completed: data.daily_questions_today || 0,
          required: data.daily_questions_required || DAILY_QUESTIONS_REQUIRED
        });
        
        // Update subscription tier
        if (data.subscription_tier) {
          setSubscriptionTier(data.subscription_tier);
        }
        
        // Check if user has completed enough daily questions
        // Auto-direct to challenge mode if warm-ups are complete
        if (data.can_access_challenges) {
          setDailyCompleted(true);
          setMode("challenge"); // Auto-switch to challenge mode
          fetchChallenges();
        }
      }
    } catch (error) {
      console.error("Error fetching daily progress:", error);
    }
  };

  // Fetch assigned drill for the user
  const fetchAssignedDrill = async () => {
    if (!user) {
      setAssignedDrill(null);
      setAssignedDrills([]);
      return;
    }
    
    try {
      const response = await fetch(`${API}/user/assigned-drill`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedDrill(data.assigned_drill);
        setAssignedDrills(data.assigned_drills || []);
      }
    } catch (error) {
      console.error("Error fetching assigned drill:", error);
    }
  };

  const fetchDailyQuestion = async () => {
    // Check if merged auth state exists in localStorage (set during login/register)
    // This is more reliable than using a ref because it survives component re-mounts
    const mergedState = localStorage.getItem('mergedAuthState');
    let shouldSkip = false;
    if (mergedState) {
      try {
        const parsed = JSON.parse(mergedState);
        // Only skip if recent (within last 30 seconds) and user should start in challenge mode
        if (Date.now() - parsed.timestamp < 30000 && parsed.startInChallengeMode) {
          shouldSkip = true;
          console.log("fetchDailyQuestion - skipping because merged auth state exists in localStorage");
        } else {
          // Clear old merged state
          localStorage.removeItem('mergedAuthState');
        }
      } catch (e) {
        console.error("Error parsing merged auth state:", e);
        localStorage.removeItem('mergedAuthState');
      }
    }
    
    // Also check the "mergedStateAppliedFlag" in localStorage (set after merged state is applied)
    const appliedFlag = localStorage.getItem('mergedStateAppliedFlag');
    if (appliedFlag) {
      try {
        const parsed = JSON.parse(appliedFlag);
        if (Date.now() - parsed.timestamp < 30000) {
          shouldSkip = true;
          console.log("fetchDailyQuestion - skipping because mergedStateAppliedFlag exists");
        } else {
          // Clear old flag
          localStorage.removeItem('mergedStateAppliedFlag');
        }
      } catch (e) {
        localStorage.removeItem('mergedStateAppliedFlag');
      }
    }
    
    console.log("fetchDailyQuestion called, shouldSkip:", shouldSkip, "mergedStateAppliedRef:", mergedStateAppliedRef.current);
    
    // Skip if merged state was already applied (to avoid overwriting challenge mode)
    // But ONLY skip if user is logged in - anonymous users should always get a question
    if ((shouldSkip || mergedStateAppliedRef.current) && user) {
      console.log("fetchDailyQuestion - skipping for logged-in user with merged state");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      // Check for a persisted question first (prevents refresh cheating)
      const persistedQuestion = loadCurrentQuestion("daily");
      if (persistedQuestion) {
        console.log("fetchDailyQuestion - using persisted question:", persistedQuestion.title);
        
        // For logged-in users, restart the blitz window server-side (fresh 3 minutes on every return)
        if (user) {
          try {
            const blitzResponse = await fetch(`${API}/questions/blitz-status/${persistedQuestion.question_id}`, {
              credentials: "include"
            });
            if (blitzResponse.ok) {
              const blitzData = await blitzResponse.json();
              // Update the persisted question with fresh blitz info
              persistedQuestion.blitz_info = {
                blitz_eligible: blitzData.blitz_eligible,
                blitz_time_remaining: blitzData.blitz_time_remaining,
                blitz_start_time: null // Not needed for display
              };
              console.log("fetchDailyQuestion - fresh blitz status:", blitzData);
            }
          } catch (blitzError) {
            console.error("Error fetching blitz status:", blitzError);
            // On error, default to no blitz bonus (safer)
            persistedQuestion.blitz_info = {
              blitz_eligible: false,
              blitz_time_remaining: 0,
              blitz_start_time: null
            };
          }
        }
        
        setDailyQuestion(persistedQuestion);
        setLoading(false);
        return;
      }
      
      // For anonymous users, pass answered question IDs to exclude
      let excludeParam = "";
      if (!user) {
        const currentSession = getSessionData();
        const answeredIds = (currentSession.answeredQuestions || [])
          .filter(q => q.pool === "daily")
          .map(q => q.question_id);
        if (answeredIds.length > 0) {
          excludeParam = `?exclude=${answeredIds.join(",")}`;
        }
      }
      
      console.log("fetchDailyQuestion - fetching from:", withMode(`${API}/questions/daily${excludeParam}`));
      const response = await fetch(withMode(`${API}/questions/daily${excludeParam}`), {
        credentials: "include"
      });
      
      if (!response.ok) {
        console.error("fetchDailyQuestion - API error:", response.status, response.statusText);
        throw new Error(`API returned ${response.status}`);
      }
      
      const data = await response.json();
      console.log("fetchDailyQuestion - received data:", data?.title, data?.question_id);
      
      if (!data || !data.question_id) {
        console.error("fetchDailyQuestion - Invalid data received:", data);
        throw new Error("Invalid question data");
      }
      
      // Validate that the question is appropriate for today's date
      // If validation fails, still show the question but log a warning
      // (This handles edge cases like timezone mismatches during deployment)
      if (!isQuestionValidForToday(data)) {
        console.warn("fetchDailyQuestion - Question date mismatch:", data.date, "- showing anyway");
      }
      
      setDailyQuestion(data);
      // Persist the question to prevent refresh cheating
      saveCurrentQuestion(data, "daily");
      
      // Update progress from response - only for logged-in users
      // For anonymous users, we track progress in session storage
      console.log("fetchDailyQuestion - checking conditions: user:", !!user, "data.daily_questions_today:", data.daily_questions_today);
      if (user && data.daily_questions_today !== undefined && !mergedStateAppliedRef.current) {
        console.log("fetchDailyQuestion - updating daily progress");
        setDailyProgress({
          completed: data.daily_questions_today,
          required: data.daily_questions_required || DAILY_QUESTIONS_REQUIRED
        });
        
        // Auto-direct to challenge mode if warm-ups are complete
        if (data.can_access_challenges) {
          setDailyCompleted(true);
          setMode("challenge"); // Auto-switch to challenge mode
          fetchChallenges();
        }
      } else if (!user) {
        // For anonymous users, check session data for challenge access
        const currentSession = getSessionData();
        if (currentSession.dailyQuestionsCompleted >= DAILY_QUESTIONS_REQUIRED) {
          setDailyCompleted(true);
          setMode("challenge"); // Auto-switch to challenge mode
          fetchChallenges();
        }
      }
    } catch (error) {
      console.error("Error fetching daily question:", error);
      // Show error toast for debugging
      toast.error("Failed to load question. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const fetchChallenges = async () => {
    try {
      // Check for a persisted challenge first (prevents refresh cheating)
      const persistedChallenge = loadCurrentQuestion("challenge");
      if (persistedChallenge) {
        console.log("fetchChallenges - using persisted challenge:", persistedChallenge.title);
        
        // For logged-in users, restart the blitz window server-side (fresh 3 minutes on every return)
        if (user) {
          try {
            const blitzResponse = await fetch(`${API}/questions/blitz-status/${persistedChallenge.question_id}`, {
              credentials: "include"
            });
            if (blitzResponse.ok) {
              const blitzData = await blitzResponse.json();
              // Update the persisted challenge with fresh blitz info
              persistedChallenge.blitz_info = {
                blitz_eligible: blitzData.blitz_eligible,
                blitz_time_remaining: blitzData.blitz_time_remaining,
                blitz_start_time: null
              };
              console.log("fetchChallenges - fresh blitz status:", blitzData);
            }
          } catch (blitzError) {
            console.error("Error fetching blitz status:", blitzError);
            // On error, default to no blitz bonus (safer)
            persistedChallenge.blitz_info = {
              blitz_eligible: false,
              blitz_time_remaining: 0,
              blitz_start_time: null
            };
          }
        }
        
        setChallenges([persistedChallenge]);
        setCurrentChallenge(persistedChallenge);
        
        // Still need to fetch challenge info for limits
        const response = await fetch(withMode(`${API}/questions/challenges?info_only=true`), {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (!user) {
            const freshSession = getSessionData();
            const sessionChallenges = freshSession.challengesCompleted || 0;
            const limit = ANONYMOUS_CHALLENGE_LIMIT;
            setChallengesInfo({
              challenges_today: sessionChallenges,
              can_do_more: sessionChallenges < limit,
              daily_limit: limit
            });
          } else {
            const limit = data.daily_limit || LOGGED_IN_CHALLENGE_LIMIT;
            setChallengesInfo({
              challenges_today: data.challenges_today || 0,
              can_do_more: data.can_do_more !== false,
              daily_limit: limit
            });
          }
        }
        return;
      }
      
      // For anonymous users, pass answered question IDs to exclude
      let excludeParam = "";
      if (!user) {
        const currentSession = getSessionData();
        const answeredIds = (currentSession.answeredQuestions || [])
          .filter(q => q.pool === "challenge")
          .map(q => q.question_id);
        if (answeredIds.length > 0) {
          excludeParam = `?exclude=${answeredIds.join(",")}`;
        }
      }
      
      const response = await fetch(withMode(`${API}/questions/challenges${excludeParam}`), {
        credentials: "include",
      });
      const data = await response.json();
      
      // Filter out any challenges with incorrect dates
      const validChallenges = (data.challenges || []).filter(isQuestionValidForToday);
      if (validChallenges.length > 0 && data.linear_progress) validChallenges[0] = { ...validChallenges[0], linear_progress: data.linear_progress };
      setChallenges(validChallenges);
      
      // Set the first valid challenge as current and persist it
      if (validChallenges.length > 0) {
        setCurrentChallenge(validChallenges[0]);
        saveCurrentQuestion(validChallenges[0], "challenge");
      }
      
      // For anonymous users, use session data for tracking
      if (!user) {
        const freshSession = getSessionData();
        const sessionChallenges = freshSession.challengesCompleted || 0;
        const limit = ANONYMOUS_CHALLENGE_LIMIT;
        setChallengesInfo({
          challenges_today: sessionChallenges,
          can_do_more: sessionChallenges < limit,
          daily_limit: limit
        });
        // Check if anonymous user hit their limit
        if (sessionChallenges >= limit) {
          setAllChallengesDone(false); // Not done, just limited
        }
      } else {
        const limit = data.daily_limit || LOGGED_IN_CHALLENGE_LIMIT;
        setChallengesInfo({
          challenges_today: data.challenges_today || 0,
          can_do_more: data.can_do_more !== false,
          daily_limit: limit
        });
        // Check if logged-in user completed all challenges for the day
        if ((data.challenges_today || 0) >= limit) {
          setAllChallengesDone(true);
        }
      }
    } catch (error) {
      console.error("Error fetching challenges:", error);
    }
  };

  const fetchNextChallenge = async () => {
    try {
      // For anonymous users, pass answered question IDs to exclude
      let excludeParam = "";
      if (!user) {
        const currentSession = getSessionData();
        const answeredIds = (currentSession.answeredQuestions || [])
          .filter(q => q.pool === "challenge")
          .map(q => q.question_id);
        if (answeredIds.length > 0) {
          excludeParam = `?exclude=${answeredIds.join(",")}`;
        }
      }
      
      const response = await fetch(withMode(`${API}/questions/challenge/next${excludeParam}`), {
        credentials: "include",
      });
      const data = await response.json();
      
      if (data.challenge) {
        // Validate that the challenge is appropriate for today
        if (isQuestionValidForToday(data.challenge)) {
          setCurrentChallenge(data.challenge);
          // Persist the new challenge to prevent refresh cheating
          saveCurrentQuestion(data.challenge, "challenge");
        } else {
          // Challenge has wrong date - clear it and show no challenge available
          console.warn("fetchNextChallenge - Challenge has wrong date:", data.challenge.date);
          setCurrentChallenge(null);
          clearCurrentQuestion();
        }
      } else {
        // No challenge returned - clear the current one
        setCurrentChallenge(null);
        clearCurrentQuestion();
      }
      
      // Update challenge info
      if (!user) {
        const sessionChallenges = getSessionData().challengesCompleted || 0;
        const limit = ANONYMOUS_CHALLENGE_LIMIT;
        setChallengesInfo({
          challenges_today: sessionChallenges,
          can_do_more: sessionChallenges < limit,
          daily_limit: limit
        });
      } else {
        const limit = data.daily_limit || LOGGED_IN_CHALLENGE_LIMIT;
        setChallengesInfo({
          challenges_today: data.challenges_today || 0,
          can_do_more: data.can_do_more !== false,
          daily_limit: limit
        });
        if ((data.challenges_today || 0) >= limit) {
          setAllChallengesDone(true);
        }
      }
    } catch (error) {
      console.error("Error fetching next challenge:", error);
    }
  };

  const handleDailyCorrect = (pointsEarned, alreadyAnswered = false, submissionResult = {}) => {
    // Check if answer was revealed from the backend response
    const wasRevealed = submissionResult.answer_revealed || false;
    setLastAnswerRevealed(wasRevealed);
    
    // For anonymous users, check session storage to determine if already answered
    let isAlreadyAnswered = alreadyAnswered;
    if (!user) {
      const freshSessionData = getSessionData();
      const answeredIds = (freshSessionData.answeredQuestions || []).map(q => q.question_id);
      isAlreadyAnswered = answeredIds.includes(dailyQuestion?.question_id);
    }
    
    // Blitz: the server decides for logged-in users (it validates the timer itself); anonymous users are worked out locally
    const earnedBlitz = user ? (submissionResult.blitz_bonus || 0) > 0 : (canEarnBlitzBonus() && !wasRevealed && !isAlreadyAnswered);
    setBlitzBonusEarned(earnedBlitz);
    setLastBlitzBonus(earnedBlitz ? BLITZ_BONUS_POINTS : 0);
    const gemBonus = wasRevealed ? 0 : (submissionResult.gem_bonus || 0);
    setLastGemBonus(gemBonus);
    
    // Calculate points
    // For logged-in users: backend already includes blitz + hidden gem bonuses in pointsEarned
    // For anonymous users: we need to add blitz bonus locally (gem bonus still comes from the graded run)
    let actualPointsEarned;
    if (user) {
      // Backend already calculated everything including blitz bonus
      actualPointsEarned = wasRevealed ? 0 : (pointsEarned || 10);
    } else {
      // Anonymous user - calculate locally
      let basePoints = wasRevealed ? 0 : (isAlreadyAnswered ? 10 : 10);
      actualPointsEarned = basePoints + (earnedBlitz ? BLITZ_BONUS_POINTS : 0) + gemBonus;
    }
    setLastPointsEarned(actualPointsEarned);
    setLastWasChallenge(false);
    setLastAlreadyAnswered(isAlreadyAnswered);
    
    // Clear the persisted question since it was answered
    clearCurrentQuestion();
    
    // Stop the timer
    setTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // For anonymous users, get the current count fresh from session storage
    let currentCompleted = dailyProgress.completed;
    if (!user) {
      const freshSessionData = getSessionData();
      currentCompleted = freshSessionData.dailyQuestionsCompleted || 0;
    }
    
    // Update progress - for logged-in users use server response, for anonymous use session tracking
    // Use !== undefined to check since server returns 0 for anonymous users
    const newCompleted = (user && submissionResult.daily_questions_today !== undefined) 
      ? submissionResult.daily_questions_today 
      : (currentCompleted + (isAlreadyAnswered ? 0 : 1));
      
    setDailyProgress(prev => ({
      ...prev,
      completed: newCompleted
    }));
    
    // For anonymous users, track in session
    if (!user) {
      const freshSessionData = getSessionData();
      const newSessionData = {
        ...freshSessionData,
        answeredQuestions: isAlreadyAnswered 
          ? freshSessionData.answeredQuestions 
          : [...freshSessionData.answeredQuestions, { question_id: dailyQuestion.question_id, pool: "daily" }],
        sessionScore: freshSessionData.sessionScore + actualPointsEarned,
        dailyQuestionsCompleted: newCompleted
      };
      setSessionData(newSessionData);
      saveSessionData(newSessionData);
    } else {
      // For logged-in users, trigger score refresh in navbar
      if (onScoreUpdate) {
        onScoreUpdate();
      }
    }
    
    // Check if user completed enough daily questions (just unlocked challenges)
    const canAccessChallenges = (user && submissionResult.can_access_challenges !== undefined)
      ? submissionResult.can_access_challenges
      : (newCompleted >= DAILY_QUESTIONS_REQUIRED);
    const justUnlockedChallenges = canAccessChallenges && !dailyCompleted;
    
    if (canAccessChallenges) {
      setDailyCompleted(true);
      fetchChallenges();
    }
    
    // Show unlock modal if just completed 3rd warm-up, otherwise show normal success
    if (justUnlockedChallenges) {
      setShowUnlockModal(true);
    } else {
      setShowSuccess(true);
    }
    // Note: New question is fetched when modal is closed (onClose handler)
  };

  // Wrapper for challenge correct that extracts streak bonus from result
  // Admin only: store the current blocks as the question's sample solution (server verifies against the tests)
  const handleSaveBlocksSolution = async (workspace, python) => {
    if (!currentQuestion?.question_id) return;
    const res = await saveBlocksSolution(currentQuestion.question_id, workspace, python, "manual");
    if (res.status === "verified") toast.success("Saved as the block sample solution");
    else toast.error(res.reason || "Could not save the block solution");
  };

  const handleChallengeCorrectWrapper = (pointsEarned, alreadyAnswered, result) => {
    const streakBonus = result?.streak_bonus || 0;
    const wasRevealed = result?.answer_revealed || false;
    handleChallengeCorrect(pointsEarned, alreadyAnswered, streakBonus, wasRevealed, result || {});
  };

  const handleChallengeCorrect = (pointsEarned, alreadyAnswered = false, streakBonusFromBackend = 0, wasRevealed = false, submissionResult = {}) => {
    const challengeQuestion = currentChallenge;
    
    // Track if answer was revealed
    setLastAnswerRevealed(wasRevealed);
    
    // For anonymous users, check session storage to determine if already answered
    let isAlreadyAnswered = alreadyAnswered;
    let currentChallengesCompleted = 0;
    if (!user && challengeQuestion) {
      const freshSessionData = getSessionData();
      const answeredIds = (freshSessionData.answeredQuestions || []).map(q => q.question_id);
      isAlreadyAnswered = answeredIds.includes(challengeQuestion.question_id);
      currentChallengesCompleted = freshSessionData.challengesCompleted || 0;
    } else if (user) {
      currentChallengesCompleted = challengesInfo.challenges_today;
    }
    
    // Blitz: the server decides for logged-in users (it validates the timer itself); anonymous users are worked out locally
    const earnedBlitz = user ? (submissionResult.blitz_bonus || 0) > 0 : (canEarnBlitzBonus() && !wasRevealed && !isAlreadyAnswered);
    setBlitzBonusEarned(earnedBlitz);
    setLastBlitzBonus(earnedBlitz ? BLITZ_BONUS_POINTS : 0);
    const gemBonus = wasRevealed ? 0 : (submissionResult.gem_bonus || 0);
    setLastGemBonus(gemBonus);
    
    // Stop the timer
    setTimerActive(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    // No points for revealed answers
    // For logged-in users, backend already includes all bonuses in pointsEarned
    // For anonymous users, we need to calculate bonuses locally
    let actualPointsEarned = wasRevealed ? 0 : (pointsEarned || 20);
    
    // For anonymous users, calculate streak and blitz bonus locally since backend doesn't track them
    // But no bonus for revealed answers
    let bonusEarned = wasRevealed ? 0 : streakBonusFromBackend;
    if (!user && !isAlreadyAnswered && !wasRevealed) {
      const newChallengesCompleted = currentChallengesCompleted + 1;
      // Streak bonus: starting from the 3rd challenge (3rd, 4th, 5th, etc.) gets +10
      if (newChallengesCompleted >= 3) {
        bonusEarned = 10;
        actualPointsEarned += bonusEarned;
      }
      
      // Add blitz bonus for anonymous users only (backend already includes it for logged-in users)
      if (earnedBlitz) {
        actualPointsEarned += BLITZ_BONUS_POINTS;
      }
    }
    // Hidden gem bonus is already inside the server's points_earned for everyone (anonymous included)
    
    setStreakBonus(bonusEarned);
    setLastPointsEarned(actualPointsEarned);
    setLastWasChallenge(true);
    setLastAlreadyAnswered(isAlreadyAnswered);
    setCompletedChallenges((prev) => new Set([...prev, currentChallengeIndex]));
    
    // Clear the persisted question since it was answered
    clearCurrentQuestion();
    
    // For anonymous users, track in session
    if (!user && challengeQuestion) {
      const freshSessionData = getSessionData();
      const newChallengesCompleted = isAlreadyAnswered 
        ? (freshSessionData.challengesCompleted || 0)
        : (freshSessionData.challengesCompleted || 0) + 1;
      const newSessionData = {
        ...freshSessionData,
        answeredQuestions: isAlreadyAnswered
          ? freshSessionData.answeredQuestions
          : [...freshSessionData.answeredQuestions, { question_id: challengeQuestion.question_id, pool: "challenge" }],
        sessionScore: freshSessionData.sessionScore + actualPointsEarned,
        challengesCompleted: newChallengesCompleted
      };
      setSessionData(newSessionData);
      saveSessionData(newSessionData);
      
      // Update challenges info for anonymous users
      setChallengesInfo((prev) => ({
        ...prev,
        challenges_today: newChallengesCompleted,
        can_do_more: newChallengesCompleted < ANONYMOUS_CHALLENGE_LIMIT
      }));
    } else if (user) {
      // For logged-in users
      const newCount = isAlreadyAnswered ? challengesInfo.challenges_today : challengesInfo.challenges_today + 1;
      setChallengesInfo((prev) => ({
        ...prev,
        challenges_today: newCount,
        can_do_more: newCount < prev.daily_limit
      }));
      
      // Check if logged-in user hit their limit
      if (newCount >= challengesInfo.daily_limit) {
        setAllChallengesDone(true);
      }
      
      // Trigger score refresh in navbar
      if (onScoreUpdate) {
        onScoreUpdate();
      }
    }
    
    setShowSuccess(true);
  };

  const handleContinueToChallenges = () => {
    setShowSuccess(false);
    setMode("challenge");
  };

  const handleLoginRequired = () => {
    setShowAuthModal(true);
  };

  // Handler for when user reveals an answer
  const handleAnswerRevealed = (questionId) => {
    setRevealedQuestions(prev => new Set([...prev, questionId]));
    // Also store in session storage for anonymous users
    const sessionData = getSessionData();
    const updatedRevealedQuestions = [...(sessionData.revealedQuestions || []), questionId];
    saveSessionData({
      ...sessionData,
      revealedQuestions: [...new Set(updatedRevealedQuestions)]
    });
  };

  const handleKeepPlaying = () => {
    setShowKeepPlayingModal(true);
  };

  const handleSignUp = () => {
    setShowKeepPlayingModal(false);
    setShowAuthModal(true);
  };

  const handleAuthSuccess = async (userData) => {
    // Read FRESH session data directly from sessionStorage (not stale React state)
    const freshSessionData = getSessionData();
    console.log("handleAuthSuccess - Fresh session data:", freshSessionData);
    
    // Capture current session state BEFORE merge clears it
    const hadWarmUpProgress = freshSessionData.dailyQuestionsCompleted >= DAILY_QUESTIONS_REQUIRED;
    const wasInChallengeMode = mode === "challenge";
    const warmUpCount = freshSessionData.dailyQuestionsCompleted || 0;
    const challengeCount = freshSessionData.challengesCompleted || 0;
    
    // Merge session data with new account
    if (freshSessionData.answeredQuestions.length > 0 || freshSessionData.sessionScore > 0 || warmUpCount > 0) {
      console.log("Calling mergeSessionAfterAuth with fresh data...");
      await mergeSessionAfterAuth(freshSessionData);
    }
    
    // Store merged state in localStorage so it survives reload
    // This ensures the frontend knows about the merged progress
    localStorage.setItem('mergedAuthState', JSON.stringify({
      warmUpProgress: warmUpCount,
      challengesCompleted: challengeCount,
      startInChallengeMode: hadWarmUpProgress || wasInChallengeMode,
      timestamp: Date.now()
    }));
    
    // Reload page to get fresh state with new user
    window.location.reload();
  };

  const mergeSessionAfterAuth = async (freshSessionData) => {
    try {
      console.log("mergeSessionAfterAuth - Sending data:", {
        answered_questions: freshSessionData.answeredQuestions || [],
        session_score: freshSessionData.sessionScore || 0,
        daily_questions_completed: freshSessionData.dailyQuestionsCompleted || 0,
        challenges_completed: freshSessionData.challengesCompleted || 0
      });
      
      const response = await fetch(`${API}/user/merge-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          mode: activeMode,
          answered_questions: freshSessionData.answeredQuestions || [],
          session_score: freshSessionData.sessionScore || 0,
          daily_questions_completed: freshSessionData.dailyQuestionsCompleted || 0,
          challenges_completed: freshSessionData.challengesCompleted || 0
        })
      });
      const result = await response.json();
      console.log("mergeSessionAfterAuth - Result:", result);
      // Clear session storage after merge
      sessionStorage.removeItem(k('dailyCodingSession'));
      return result;
    } catch (error) {
      console.error("Error merging session:", error);
      return null;
    }
  };

  const currentQuestion = mode === "daily" 
    ? dailyQuestion 
    : currentChallenge;

  const today = new Date().toLocaleDateString("en-AU", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "Australia/Sydney"
  });

  // Check if anonymous user hit their limit
  const anonymousLimitReached = !user && (sessionData.challengesCompleted || 0) >= ANONYMOUS_CHALLENGE_LIMIT;
  // Check if logged-in user hit their limit
  const loggedInLimitReached = user && !challengesInfo.can_do_more;
  
  const canSubmitChallenge = !anonymousLimitReached && !loggedInLimitReached;

  // Calculate session score for display
  const displayScore = user ? null : sessionData.sessionScore;

  // Handle starting drill mode - accepts a specific drill to start
  const handleStartDrill = (drill = null) => {
    const drillToStart = drill || assignedDrill;
    if (drillToStart && drillToStart.status !== "completed") {
      setSelectedDrill(drillToStart);
      setDrillMode(true);
    }
  };

  // Handle drill exit
  const handleDrillExit = () => {
    setDrillMode(false);
    setSelectedDrill(null);
    // Refresh assigned drill status
    fetchAssignedDrill();
  };

  // Handle drill completion
  const handleDrillComplete = () => {
    // Refresh the assigned drill to show completed status
    fetchAssignedDrill();
    setSelectedDrill(null);
    // Also refresh daily progress
    if (user) {
      fetchDailyProgress();
    }
  };

  // If in drill mode, show the drill interface
  if (drillMode && selectedDrill) {
    return (
      <DrillMode
        drill={selectedDrill}
        onExit={handleDrillExit}
        user={user}
        onDrillComplete={handleDrillComplete}
      />
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="daily-challenge-page">
      {/* One-time tooltip tour: mode switch -> Test/Submit buttons (starts once a question is on screen) */}
      <FirstVisitTour ready={!loading && !!currentQuestion && !showCaveBat} />
      <div className="max-w-7xl mx-auto">
        {/* Header - Using extracted ProgressHeader component */}
        <ProgressHeader
          mode={mode}
          today={today}
          dailyProgress={dailyProgress}
          challengesInfo={challengesInfo}
          displayScore={displayScore}
          user={user}
          dailyCompleted={dailyCompleted}
          onBackToWarmup={() => setMode("daily")}
          streakCount={challengesInfo.challenges_today}
          assignedDrill={assignedDrill}
          assignedDrills={assignedDrills}
          onStartDrill={handleStartDrill}
          onPlayCaveBat={() => setShowCaveBat(true)}
        />

        {/* Cave Bat Game - Manually triggered from button (when user has 3+ challenges but not at limit) */}
        {showCaveBat && mode === "challenge" && user && !loggedInLimitReached && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto space-y-6"
          >
            <div className="text-center py-6">
              <span className="text-4xl mb-4 block">🦇</span>
              <h2 className="text-2xl font-bold mb-2">Cave Bat Break!</h2>
              <p className="text-muted-foreground mb-4">
                Take a quick break and play Cave Bat!
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCaveBat(false)}
                className="mb-6"
                data-testid="back-to-challenges-btn"
              >
                Back to Challenges
              </Button>
            </div>
            
            <FlappyBat user={user} />
          </motion.div>
        )}

        {/* Logged-in user completed all challenges for the day - Show FlappyBat Game */}
        {mode === "challenge" && user && loggedInLimitReached && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto space-y-6"
          >
            {/* Upgrade Prompt for Free Users */}
            {subscriptionTier === "free" && (
              <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-yellow-500/10 border border-amber-500/30 rounded-2xl p-6">
                <div className="text-center mb-4">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 text-sm font-medium mb-3">
                    <Zap className="w-4 h-4" />
                    Unlock More Challenges
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    Want to keep practicing?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Upgrade to Student and supercharge your coding journey!
                  </p>
                </div>
                
                <div className="space-y-2 mb-5">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-foreground"><strong>8 challenges/day</strong> instead of 3</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-foreground"><strong>Unlock hints</strong> when you're stuck</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-foreground"><strong>Support the platform</strong> and help us grow</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                      <svg className="w-3 h-3 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-foreground"><strong>Climb the leaderboard</strong> faster</span>
                  </div>
                </div>
                
                <a
                  href="/subscription"
                  className="block w-full text-center py-3 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/25"
                  data-testid="upgrade-btn-flappy"
                >
                  Upgrade to Student - $8/month
                </a>
              </div>
            )}
            
            {/* Countdown to Next Day */}
            <div className="bg-card/50 backdrop-blur-sm border border-border/40 rounded-2xl p-6 text-center mb-6">
              <p className="text-sm text-muted-foreground mb-3">New challenges available in</p>
              <div className="flex justify-center gap-4">
                <div className="bg-primary/10 rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-2xl font-bold text-primary font-mono">
                    {String(countdownToMidnight.hours).padStart(2, '0')}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">hours</div>
                </div>
                <div className="bg-primary/10 rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-2xl font-bold text-primary font-mono">
                    {String(countdownToMidnight.minutes).padStart(2, '0')}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">mins</div>
                </div>
                <div className="bg-primary/10 rounded-xl px-4 py-3 min-w-[70px]">
                  <div className="text-2xl font-bold text-primary font-mono">
                    {String(countdownToMidnight.seconds).padStart(2, '0')}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">secs</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3">Play Cave Bat while you wait!</p>
            </div>
            
            <FlappyBat user={user} />
          </motion.div>
        )}

        {/* Daily Limit Warning */}
        {mode === "challenge" && user && !challengesInfo.can_do_more && !loggedInLimitReached && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-6"
          >
            <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/10 border border-secondary/20">
              <AlertCircle className="w-5 h-5 text-secondary shrink-0" />
              <p className="text-sm text-foreground">
                You've reached your daily limit of {challengesInfo.daily_limit} bonus challenges. 
                New challenges in{" "}
                <span className="font-mono font-semibold text-primary">
                  {String(countdownToMidnight.hours).padStart(2, '0')}:
                  {String(countdownToMidnight.minutes).padStart(2, '0')}:
                  {String(countdownToMidnight.seconds).padStart(2, '0')}
                </span>
              </p>
            </div>
          </motion.div>
        )}

        {/* Main Content - Hide when logged-in limit reached OR when Cave Bat is being played */}
        {!(mode === "challenge" && user && loggedInLimitReached) && !showCaveBat && (
        <div
          className={isBlockMode(currentQuestion?.mode) ? "flex flex-col gap-6" : "grid lg:grid-cols-2 gap-8"}
          data-testid={isBlockMode(currentQuestion?.mode) ? "question-layout-stacked" : "question-layout-split"}
        >
          {/* Question Card - Blocks questions stack it above a full-width workspace so there's more room to build */}
          <motion.div
            initial={{ opacity: 0, ...(isBlockMode(currentQuestion?.mode) ? { y: -12 } : { x: -20 }) }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            {loading ? (
              <div className="question-card animate-gentle-pulse">
                <div className="h-6 bg-muted rounded w-1/3 mb-4" />
                <div className="h-4 bg-muted rounded w-full mb-2" />
                <div className="h-4 bg-muted rounded w-2/3 mb-6" />
                <div className="h-20 bg-muted rounded" />
              </div>
            ) : (
              <QuestionCard
                question={currentQuestion}
                showNavigation={false}
                currentIndex={currentChallengeIndex}
                totalCount={challenges.length}
                onNext={() => {}}
                onPrev={() => {}}
                user={user}
                subscriptionTier={subscriptionTier}
                onLoginRequired={handleLoginRequired}
                onAnswerRevealed={handleAnswerRevealed}
                compact={isBlockMode(currentQuestion?.mode)}
                progress={mode === "challenge"
                  ? { done: challengesInfo.challenges_today, total: challengesInfo.daily_limit }
                  : { done: dailyProgress.completed, total: dailyProgress.required }}
              />
            )}
          </motion.div>

          {/* Code Editor */}
          <motion.div
            initial={{ opacity: 0, ...(isBlockMode(currentQuestion?.mode) ? { y: 12 } : { x: 20 }) }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {loading ? (
              <div className="bg-card rounded-2xl border border-border p-6 animate-gentle-pulse">
                <div className="h-64 bg-muted rounded-xl mb-4" />
                <div className="h-24 bg-muted rounded-xl mb-4" />
                <div className="flex gap-3">
                  <div className="h-11 bg-muted rounded-xl flex-1" />
                  <div className="h-11 bg-muted rounded-xl flex-1" />
                </div>
              </div>
            ) : (
              <div className="bg-card rounded-2xl border border-border/40 p-6">
                <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                  <h3 className="font-heading font-semibold text-lg flex items-center gap-2" data-testid="editor-heading">
                    {currentQuestion?.mode === "junior" || currentQuestion?.blocks?.kind === "nav" ? (
                      <><span className="text-primary">Drill</span> Navigation</>
                    ) : (
                      <><span className="text-primary">{isBlockMode(currentQuestion?.mode) ? "Blocks" : "Python"}</span> Code Editor</>
                    )}
                    {isBlockMode(currentQuestion?.mode) && (
                      <button
                        type="button"
                        onClick={replayTour}
                        className="ml-1 inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium text-muted-foreground hover:text-primary hover:border-primary/60 hover:bg-primary/5 transition-colors"
                        title="How to play - show the quick tips again"
                        aria-label="How to play"
                        data-testid="how-to-play-btn"
                      >
                        <CircleHelp className="w-4 h-4" /> <span className="hidden sm:inline">How to play</span>
                      </button>
                    )}
                  </h3>
                  {/* Blitz bonus counter lives with the code so it's in view while typing / building */}
                  {currentQuestion && (
                    <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="blitz-counter">
                      <TimerDisplay timeRemaining={timeRemaining} maxTime={BLITZ_TIMER_SECONDS} isChallenge={mode === "challenge"} />
                      {timeRemaining > 0 ? (
                        <span data-testid="blitz-bonus-active" className="text-xs font-medium text-muted-foreground">
                          Blitz Bonus: +{BLITZ_BONUS_POINTS}pts
                        </span>
                      ) : !showSuccess && (
                        <span data-testid="blitz-bonus-expired" className="text-xs text-muted-foreground">
                          No Blitz Bonus, but you can still earn points!
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {/* Show Code Editor or Keep Playing button based on limits */}
                {mode === "challenge" && anonymousLimitReached ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground mb-4">
                      You've used all {ANONYMOUS_CHALLENGE_LIMIT} of your daily challenge attempts.
                    </p>
                    <Button
                      onClick={handleKeepPlaying}
                      className="gap-2 rounded-xl px-8 h-12 bg-primary hover:bg-primary/90"
                      data-testid="keep-playing-btn"
                    >
                      Keep Playing
                    </Button>
                  </div>
                ) : (
                  <>
                    <CodeWorkspace
                      key={`${currentQuestion?.question_id}-${submissionCount}`}
                      question={currentQuestion}
                      questionId={currentQuestion?.question_id}
                      exampleInput={currentQuestion?.example_input}
                      onCorrect={mode === "daily" ? handleDailyCorrect : handleChallengeCorrectWrapper}
                      darkMode={darkMode}
                      disabled={mode === "challenge" && !canSubmitChallenge}
                      answerRevealed={currentQuestion?.question_id ? revealedQuestions.has(currentQuestion.question_id) : false}
                      timeRemaining={timeRemaining}
                      onSaveAsSolution={user?.is_admin && currentQuestion?.mode === "blocks" ? handleSaveBlocksSolution : undefined}
                      layout={isBlockMode(currentQuestion?.mode) ? "wide" : "split"}
                    />
                    {mode === "challenge" && !canSubmitChallenge && (
                      <p className="text-sm text-muted-foreground mt-4 text-center">
                        Daily limit reached. You can still view questions and test code.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </motion.div>
        </div>
        )}

        {/* Challenges Unlock Section */}
        {mode === "daily" && !dailyCompleted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-3 px-6 py-4 bg-muted/50 rounded-2xl">
              <Lock className="w-5 h-5 text-muted-foreground" />
              <span className="text-muted-foreground">
                Complete {dailyProgress.required - dailyProgress.completed} more warm up question{dailyProgress.required - dailyProgress.completed !== 1 ? 's' : ''} to unlock bonus challenges!
              </span>
            </div>
          </motion.div>
        )}

        {/* Challenges Available Button */}
        {mode === "daily" && dailyCompleted && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-12 text-center"
          >
            <Button
              onClick={() => setMode("challenge")}
              className="gap-2 rounded-xl px-8 h-12 bg-secondary hover:bg-secondary/90"
              data-testid="go-to-challenges-btn"
            >
              <Zap className="w-5 h-5" />
              Try Bonus Challenges ({challenges.length} available)
            </Button>
          </motion.div>
        )}
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          // Increment submission counter to force CodeEditor reset
          setSubmissionCount(prev => prev + 1);
          // Clear the persisted question so we fetch a fresh one
          clearCurrentQuestion();
          // Advance to next question when modal is closed
          if (mode === "daily") {
            if (!dailyCompleted) {
              // Still doing warm-ups - fetch next daily question
              fetchDailyQuestion();
            }
            // If dailyCompleted is true, the modal will show "Continue to Challenges" button
            // which uses onContinue handler instead
          } else if (mode === "challenge") {
            // Fetch the next challenge question from the backend
            fetchNextChallenge();
          }
        }}
        onContinue={handleContinueToChallenges}
        showChallenges={mode === "daily" && dailyCompleted}
        user={user}
        pointsEarned={lastPointsEarned}
        isChallenge={lastWasChallenge}
        alreadyAnswered={lastAlreadyAnswered}
        sessionScore={!user ? sessionData.sessionScore : null}
        streakBonus={streakBonus}
        answerRevealed={lastAnswerRevealed}
        blitzBonus={lastBlitzBonus}
        gemBonus={lastGemBonus}
        challengesCompleted={challengesInfo.challenges_today}
        challengeLimit={challengesInfo.daily_limit}
        subscriptionTier={subscriptionTier}
        onCreateAccount={() => setShowAuthModal(true)}
      />

      {/* Challenge Unlocked Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setShowUnlockModal(false);
              setMode("challenge");
            }}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative bg-card rounded-3xl border-2 border-secondary/30 p-10 max-w-lg mx-4 text-center shadow-2xl"
          >
            {/* Decorative background */}
            <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 to-primary/5 rounded-3xl" />
            
            <div className="relative">
              {/* Trophy Icon with glow */}
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 400 }}
                className="mx-auto mb-6 p-5 rounded-full bg-gradient-to-br from-secondary/20 to-secondary/10 w-fit shadow-lg shadow-secondary/20"
              >
                <Trophy className="w-16 h-16 text-secondary" />
              </motion.div>

              {/* Stars decoration */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="flex justify-center gap-2 mb-4"
              >
                <Star className="w-6 h-6 text-secondary fill-secondary" />
                <Star className="w-8 h-8 text-secondary fill-secondary" />
                <Star className="w-6 h-6 text-secondary fill-secondary" />
              </motion.div>

              {/* Title */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-2 flex items-center justify-center gap-3">
                  <Sparkles className="w-7 h-7 text-secondary" />
                  <span className="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">
                    Challenge Questions Unlocked!
                  </span>
                  <Sparkles className="w-7 h-7 text-secondary" />
                </h2>
                <p className="text-muted-foreground text-lg mt-3 mb-2">
                  You completed all {DAILY_QUESTIONS_REQUIRED} warm-up questions!
                </p>
                <p className="text-sm text-muted-foreground">
                  Now tackle {challengesInfo.daily_limit} bonus challenges for extra points
                </p>
              </motion.div>

              {/* Points earned */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="mt-6 mb-8"
              >
                <div className="inline-flex items-center gap-2 px-5 py-3 bg-secondary/10 rounded-full border border-secondary/20">
                  <Zap className="w-5 h-5 text-secondary" />
                  <span className="text-lg font-bold text-secondary">+{lastPointsEarned} points earned!</span>
                </div>
              </motion.div>

              {/* Action Button */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 }}
              >
                <Button
                  onClick={() => {
                    setShowUnlockModal(false);
                    setMode("challenge");
                  }}
                  className="gap-2 rounded-xl px-10 h-14 text-lg bg-secondary hover:bg-secondary/90 shadow-lg shadow-secondary/25"
                  data-testid="start-challenges-btn"
                >
                  <Zap className="w-5 h-5" />
                  Start Challenges
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Keep Playing Modal for Anonymous Users */}
      {showKeepPlayingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowKeepPlayingModal(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative bg-card rounded-2xl border border-border p-8 max-w-md mx-4 text-center shadow-xl"
          >
            <div className="mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Want to Keep Playing?</h3>
              <p className="text-muted-foreground">
                Sign up to answer more questions today, or come back tomorrow for {ANONYMOUS_CHALLENGE_LIMIT} more free challenges!
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                onClick={handleSignUp}
                className="w-full gap-2 rounded-xl h-12 bg-primary hover:bg-primary/90"
                data-testid="signup-btn"
              >
                Sign Up for More
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowKeepPlayingModal(false)}
                className="w-full rounded-xl h-12"
                data-testid="close-modal-btn"
              >
                Maybe Tomorrow
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cave Bat Easter Egg Overlay (Konami code) */}
      {showCaveBatEgg && user && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          data-testid="cave-bat-easter-egg-overlay"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowCaveBatEgg(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative bg-card rounded-2xl border border-border shadow-2xl p-6 w-full max-w-lg max-h-[92vh] overflow-y-auto"
            data-testid="cave-bat-easter-egg"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🦇</span>
                <h2 className="text-lg font-bold">Secret Cave Bat!</h2>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowCaveBatEgg(false)}
                data-testid="close-cave-bat-egg-btn"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <FlappyBat user={user} />
            <p className="text-center text-xs text-muted-foreground mt-3">
              You unlocked the secret code! Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-foreground">Esc</kbd> to close.
            </p>
          </motion.div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        initialMode="register"
      />
    </div>
  );
}
